/* eslint-disable max-lines */
import { CbhIcon, DotSeparatedList, Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack, type Theme, useTheme } from "@mui/material";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { addHours, isBefore, isSameMinute, parseISO, startOfMinute, startOfSecond } from "date-fns";
import { useHistory } from "react-router-dom";

import { Button } from "../../components/Button";
import { formatDateRangeDuration } from "../../utils/formatDateRangeDuration";
import { formatTimeWithTimezoneDisambiguation } from "../../utils/formatTimeWithTimezoneDisambiguation";
import { OverlappingShiftWorkersAvatars } from "../../WorkWithFriends/components/OverlappingShiftWorkersAvatars";
import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { CurrentShiftStage } from "../constants";
import { type TimeSlot } from "../Open/types";
import { TimeSlotIcon } from "../TimeSlotIcon";
import { HighlightedWorkerShiftCardWrapper } from "./HighlightedCardWrapper";

export interface HighlightedWorkerShiftCardProps {
  href: string;
  workplaceName: string;
  workplaceTimezone: string;
  workerTimezone: string;
  currentDate: Date;
  targetArrivalTime?: string;
  shiftStart: string;
  shiftEnd: string;
  shiftPosition: string;
  shiftHourlyPay?: string;
  shiftTotalPay?: string;
  shiftTotalWithBoostPay?: string;
  shiftStage: CurrentShiftStage;
  shiftTimeSlot: TimeSlot;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
  isInstantPayShift: boolean;
  isMissedPunchRequestEnabled?: boolean;
}
function getShiftStatusParameters(props: {
  workplaceName: string;
  workplaceTimezone: string;
  workerTimezone: string;
  shiftStage: CurrentShiftStage;
  shiftQualificationDisplayLabel: string;
  startDate: Date;
  endDate: Date;
  targetArrivalTime?: string;
  currentDate: Date;
  theme: Theme;
  isInstantPayShift: boolean;
  isMissedPunchRequestEnabled?: boolean;
}) {
  const {
    workplaceName,
    workplaceTimezone,
    workerTimezone,
    shiftStage,
    shiftQualificationDisplayLabel,
    startDate,
    endDate,
    targetArrivalTime,
    currentDate,
    theme,
    isInstantPayShift,
    isMissedPunchRequestEnabled,
  } = props;

  const pillColor = theme.palette.background.tertiary;
  let titleText = "";
  let pillText = "";
  let buttonText = "View details";

  const formattedStartTime = formatTimeWithTimezoneDisambiguation({
    time: startDate,
    displayInTimezone: workplaceTimezone,
    compareToTimezone: workerTimezone,
  });

  const formattedTargetArrivalTime = isDefined(targetArrivalTime)
    ? formatTimeWithTimezoneDisambiguation({
        time: parseISO(targetArrivalTime),
        displayInTimezone: workplaceTimezone,
        compareToTimezone: workerTimezone,
      })
    : undefined;

  const shiftDetails = `${shiftQualificationDisplayLabel} shift at ${workplaceName}`;
  const shiftDetailsWithStartTime = `${formattedStartTime} ${shiftDetails}`;

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (shiftStage) {
    case CurrentShiftStage.NOT_STARTED: {
      return {
        pillColor,
        ...getShiftNotStartedParameters({
          shiftDetails,
          isInstantPayShift,
          isMissedPunchRequestEnabled,
          startDate,
          formattedTargetArrivalTime,
          currentDate,
          shiftDetailsWithStartTime,
        }),
      };
    }

    case CurrentShiftStage.CLOCKED_IN:
    case CurrentShiftStage.BREAK_STARTED:
    case CurrentShiftStage.BREAK_ENDED: {
      const now = startOfSecond(currentDate);
      const isShiftEnded = isBefore(endDate, now);

      if (isShiftEnded) {
        const timePastEnd = formatDateRangeDuration({
          startDate: endDate,
          endDate: now,
        });
        pillText = "In progress";
        titleText = `It is ${timePastEnd} past scheduled shift end. Don't forget to clock out.`;
        buttonText = "View details and clock out";
      } else {
        const endsIn = formatDateRangeDuration({
          startDate: now,
          endDate,
        });
        pillText = "In progress";
        titleText = `Your shift has ${endsIn} remaining`;
      }

      break;
    }

    case CurrentShiftStage.CLOCKED_OUT: {
      pillText = "Clocked out";
      titleText = "Submit your timesheet before you leave the workplace";
      buttonText = "Submit timesheet";

      break;
    }

    case CurrentShiftStage.PENDING_VERIFICATION: {
      pillText = "Clocked out";
      titleText = `Timesheet is pending verification for your ${shiftDetailsWithStartTime}`;
      break;
    }

    case CurrentShiftStage.VERIFIED: {
      pillText = "Verified";
      titleText = `Timesheet has been verified for your ${shiftDetailsWithStartTime}`;
      break;
    }

    default:
  }

  return { titleText, pillText, pillColor, buttonText };
}

function getShiftNotStartedParameters(params: {
  isInstantPayShift: boolean;
  isMissedPunchRequestEnabled?: boolean;
  startDate: Date;
  formattedTargetArrivalTime?: string;
  currentDate: Date;
  shiftDetailsWithStartTime: string;
  shiftDetails: string;
}) {
  const {
    isInstantPayShift,
    isMissedPunchRequestEnabled,
    startDate,
    currentDate,
    shiftDetailsWithStartTime,
    shiftDetails,
    formattedTargetArrivalTime,
  } = params;

  const startsIn = formatDateRangeDuration({
    startDate: startOfSecond(currentDate),
    endDate: startDate,
  });
  const now = startOfMinute(currentDate);
  const isShiftStarted = isBefore(startDate, now) || isSameMinute(startDate, now);
  const isWithinOneHour = isBefore(startDate, addHours(now, 1));

  let titleText = "";
  let pillText = "";
  let buttonText = "View details";

  if (isShiftStarted) {
    pillText = "Shift started";

    if (isInstantPayShift) {
      titleText = `Clock in to your ${shiftDetailsWithStartTime}`;
      buttonText = "View details and clock in";
    } else if (isMissedPunchRequestEnabled) {
      titleText = `Request shift payment for your ${shiftDetailsWithStartTime}`;
    } else {
      titleText = `Upload timesheet for your ${shiftDetailsWithStartTime}`;
    }
  } else {
    pillText = `Starts in ${startsIn}`;
    titleText = `Prepare for your ${shiftDetailsWithStartTime}`;

    if (isInstantPayShift && isWithinOneHour) {
      buttonText = "View details and clock in";
    }
  }

  if (isDefined(formattedTargetArrivalTime)) {
    titleText = `Arrive by ${formattedTargetArrivalTime} to your ${shiftDetails}`;
  }

  return { titleText, pillText, buttonText };
}

export function HighlightedWorkerShiftCard(props: HighlightedWorkerShiftCardProps) {
  const {
    href,
    workplaceName,
    workplaceTimezone,
    workerTimezone,
    shiftStart,
    shiftEnd,
    shiftPosition,
    shiftHourlyPay,
    shiftTotalPay,
    shiftTotalWithBoostPay,
    shiftStage,
    shiftTimeSlot,
    currentDate,
    workersForOverlappingShiftSlot,
    isInstantPayShift,
    isMissedPunchRequestEnabled,
    targetArrivalTime,
  } = props;

  const theme = useTheme();

  const startDate = parseISO(shiftStart);
  const endDate = parseISO(shiftEnd);

  const history = useHistory();

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([shiftPosition]);
  const qualificationDisplayLabel =
    qualificationNameToDisplayLabelMap.get(shiftPosition) ?? shiftPosition;

  const { titleText, pillText, pillColor, buttonText } = getShiftStatusParameters({
    shiftStage,
    shiftQualificationDisplayLabel: qualificationDisplayLabel,
    startDate,
    endDate,
    currentDate,
    workplaceName,
    workplaceTimezone,
    workerTimezone,
    theme,
    isInstantPayShift,
    isMissedPunchRequestEnabled,
    targetArrivalTime,
  });

  return (
    <HighlightedWorkerShiftCardWrapper shiftTimeSlot={shiftTimeSlot}>
      <CardContent
        onClick={() => {
          history.push(href);
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Pill size="medium" label={pillText} backgroundColor={() => pillColor} />
          <Stack direction="row" alignItems="center" spacing={1}>
            {isDefined(workersForOverlappingShiftSlot) && (
              <OverlappingShiftWorkersAvatars
                overlappingShiftWorkers={workersForOverlappingShiftSlot}
                profileImageSizeInPx={32}
              />
            )}
            <TimeSlotIcon timeSlot={shiftTimeSlot} size="medium" />
          </Stack>
        </Stack>

        <Stack direction="column" spacing={4} marginTop={12}>
          <Text variant="h5">{titleText}</Text>
          {isDefined(shiftHourlyPay) && isDefined(shiftTotalPay) && (
            <DotSeparatedList sx={{ alignItems: "center", color: theme.palette.text.secondary }}>
              <Text semibold variant="body2" sx={{ color: "inherit" }}>
                {`${shiftHourlyPay} /hr`}
              </Text>
              <>
                {isDefined(shiftTotalWithBoostPay) && <CbhIcon type="bolt-filled" size="xSmall" />}
                <Text semibold variant="body2" sx={{ color: "inherit" }}>
                  {shiftTotalWithBoostPay ?? shiftTotalPay} total
                </Text>
              </>
            </DotSeparatedList>
          )}
          <Button
            role="button"
            variant="contained"
            onClick={(event) => {
              event.stopPropagation();
              history.push(href);
            }}
          >
            {buttonText}
          </Button>
        </Stack>
      </CardContent>
    </HighlightedWorkerShiftCardWrapper>
  );
}
/* eslint-enable max-lines */
